import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Popover,
  Tab,
  Typography,
} from '@mui/material';
import { Clear, Check } from '@mui/icons-material';
import { FC } from 'react';
import { t } from 'i18next';
import { TabContext, TabList } from '@mui/lab';
import { useNotificationPopover } from 'components/Header/components/Notifications/hooks';
import { NotificationItem } from 'components/Header/components/Notifications/NotificationItem.tsx';
import { ceil } from 'lodash';

interface NotificationPopoverProps {
  isOpen: boolean;
  id: string | undefined;
  anchorEl: HTMLElement;
  onClose: () => void;
  unreadNotificationCount: number;
}

export const NotificationPopover: FC<NotificationPopoverProps> = ({
  isOpen,
  id,
  anchorEl,
  onClose,
  unreadNotificationCount,
}) => {
  const {
    tabIndex,
    handleChangeTab,
    entries,
    handlePageChange,
    totalCount,
    handleMarkNotification,
    handleMarkAllNotification,
    isMarkAllLoading,
    handleNotificationItemClick,
    filters,
    isLoading,
  } = useNotificationPopover({ onClose });

  return (
    <Popover
      open={isOpen}
      id={id}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      sx={{
        zIndex: 100,
      }}
    >
      <Box
        sx={{
          width: 400,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='h6'>
            {t('header.notifications.title')}
          </Typography>
          <IconButton onClick={onClose}>
            <Clear />
          </IconButton>
        </Box>
        <TabContext value={tabIndex}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <TabList onChange={handleChangeTab}>
              <Tab
                label={t('header.notifications.popover.unread', {
                  count: unreadNotificationCount,
                })}
                value='unread'
              />
              <Tab label={t('header.notifications.popover.all')} value='all' />
            </TabList>
          </Box>
        </TabContext>

        {entries.length > 0 && !isLoading ? (
          <>
            <Box
              sx={{
                height: 400,
                overflowY: 'auto',
              }}
            >
              {entries.map((entry, idx) => {
                return (
                  <NotificationItem
                    lastItem={idx === entries.length - 1}
                    key={idx}
                    handleMarkNotification={handleMarkNotification}
                    handleNotificationItemClick={handleNotificationItemClick}
                    {...entry}
                  />
                );
              })}
            </Box>
            <Pagination
              count={ceil(totalCount / 10)}
              page={filters.page}
              onChange={(e, number) => handlePageChange(number)}
            />
          </>
        ) : (
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              height: 400,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                alignItems: 'center',
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress
                    sx={{
                      height: 40,
                      width: 40,
                    }}
                    color='primary'
                  />
                  <Typography variant='h6' sx={{ textTransform: 'capitalize' }}>
                    {t('general.loading')}
                  </Typography>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      height: 46,
                      width: 46,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: theme => theme.palette.primary.main,
                      borderRadius: '50%',
                    }}
                  >
                    <Check
                      sx={{
                        height: 40,
                        width: 40,
                        color: theme => theme.palette.common.white,
                      }}
                    />
                  </Box>
                  <Typography variant='h6'>
                    {t('header.notifications.popover.noUnreadMessage')}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        <Button
          variant='text'
          startIcon={<Check />}
          onClick={handleMarkAllNotification}
          disabled={isMarkAllLoading || unreadNotificationCount === 0}
        >
          {t('header.notifications.popover.markAllAsRead')}
        </Button>
      </Box>
    </Popover>
  );
};
